import dayjs from 'dayjs';
import { dateUtils } from 'shared';
import type { flightEntity } from 'freedom-shared';

import type {
  BackwardTimesMapping,
  DayObject,
  ForwardTimesMapping,
  TimesMappingEntry,
  TimesMappingWithSets,
} from '../types';

function formatDate(date: string | Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/**
 * Creates a time key from time and date
 * Format: "HH:mm-YYYY-MM-DD"
 */
function createTimeKey(time: string, date: string | Date): string {
  return `${time}-${formatDate(date)}`;
}

/**
 * Parses key "HH:mm-YYYY-MM-DD" into { time, date }.
 * Date format YYYY-MM-DD contains dashes, so we match it at the end.
 */
function parseTimeKey(key: string): { time: string; date: string } {
  const match = key.match(/-(\d{4}-\d{2}-\d{2})$/);
  if (match) {
    return { time: key.slice(0, -11), date: match[1] };
  }
  return { time: key, date: '' };
}

/**
 * Creates a day object with formatted date and sorted times
 */
function createDayObject(date: string, times: string[]): DayObject | null {
  if (!date || times.length === 0) {
    return null;
  }

  return {
    date,
    dateFormatted: dateUtils.getVerbalDate(dayjs(date)),
    times: [...times].sort(),
  };
}

/**
 * Converts times mapping from Sets to sorted arrays.
 */
function convertTimesMapping(
  mapping: TimesMappingWithSets,
  allTimesSet: Set<string>,
): Record<string, TimesMappingEntry> {
  const allTimesSorted = Array.from(allTimesSet).sort();
  const result: Record<string, TimesMappingEntry> = {};

  Object.entries(mapping).forEach(([key, data]) => {
    result[key] = {
      date: data.date,
      dateFormatted: data.dateFormatted,
      times: allTimesSorted,
      availableTimes: Array.from(data.times).sort(),
    };
  });

  return result;
}

/**
 * Inverts backwardTimesByForwardTime to produce forwardTimesByBackwardTime.
 * For each (forwardKey, { date: backwardDate, availableTimes }) we add forwardTime
 * to forwardTimesByBackwardTime[backwardKey] for each backwardTime in availableTimes.
 * Value's date is forward date (from forwardKey).
 */
function invertTimesMapping(
  backwardMapping: Record<string, TimesMappingEntry>,
  allForwardTimes: Set<string>,
): ForwardTimesMapping {
  const forwardMapping: TimesMappingWithSets = {};

  Object.entries(backwardMapping).forEach(([forwardKey, backwardData]) => {
    const { time: forwardTime, date: forwardDate } = parseTimeKey(forwardKey);

    backwardData.availableTimes.forEach(backwardTime => {
      const backwardKey = createTimeKey(backwardTime, backwardData.date);
      if (!forwardMapping[backwardKey]) {
        forwardMapping[backwardKey] = {
          date: forwardDate,
          dateFormatted: dateUtils.getVerbalDate(dayjs(forwardDate)),
          times: new Set<string>(),
        };
      }
      forwardMapping[backwardKey].times.add(forwardTime);
    });
  });

  return convertTimesMapping(
    forwardMapping,
    allForwardTimes,
  ) as ForwardTimesMapping;
}

/**
 * Processes a single offer to extract forward and backward flight information.
 * Only fills backwardTimesByForwardTime; forwardTimesByBackwardTime is derived via invertTimesMapping.
 */
function processOfferForRT(
  offer: flightEntity.types.MappedOffer,
  forwardTimesSet: Set<string>,
  backwardTimesSet: Set<string>,
  backwardTimesByForwardTime: TimesMappingWithSets,
): {
  forwardDate: string | null;
  backwardDate: string | null;
} {
  const forwardDep = offer.flights[0]?.segments[0]?.dep;
  const backwardDep = offer.flights[1]?.segments[0]?.dep;

  const forwardDate = forwardDep ? formatDate(forwardDep.at) : null;
  const backwardDate = backwardDep ? formatDate(backwardDep.at) : null;

  if (forwardDep) {
    forwardTimesSet.add(forwardDep.time);
  }

  if (backwardDep && forwardDep && backwardDate) {
    backwardTimesSet.add(backwardDep.time);

    const forwardKey = createTimeKey(forwardDep.time, forwardDep.at);

    if (!backwardTimesByForwardTime[forwardKey]) {
      backwardTimesByForwardTime[forwardKey] = {
        date: backwardDate,
        dateFormatted: dateUtils.getVerbalDate(dayjs(backwardDep.at)),
        times: new Set<string>(),
      };
    }
    backwardTimesByForwardTime[forwardKey].times.add(backwardDep.time);
  }

  return { forwardDate, backwardDate };
}

/**
 * Creates day objects for Round Trip (RT) flights.
 * Returns two day groups (forward and backward) and a mapping of backward times
 * available for each forward time+date combination.
 */
export function makeDaysRT(offers: flightEntity.types.MappedOffer[]): {
  days: DayObject[];
  backwardTimesByForwardTime: BackwardTimesMapping;
  forwardTimesByBackwardTime: ForwardTimesMapping;
} {
  const forwardTimesSet = new Set<string>();
  const backwardTimesSet = new Set<string>();
  const backwardTimesByForwardTime: TimesMappingWithSets = {};

  // Process all offers to collect times and build mapping
  // Track first valid dates encountered - needed to create day objects later.
  // All forward flights share the same date, and all backward flights share the same date,
  // but we need to find the first valid date in case the first offer has null/undefined dates.
  let firstForwardDate: string | null = null;
  let firstBackwardDate: string | null = null;

  offers.forEach(offer => {
    const { forwardDate, backwardDate } = processOfferForRT(
      offer,
      forwardTimesSet,
      backwardTimesSet,
      backwardTimesByForwardTime,
    );

    // Track first valid dates encountered
    if (forwardDate && !firstForwardDate) {
      firstForwardDate = forwardDate;
    }
    if (backwardDate && !firstBackwardDate) {
      firstBackwardDate = backwardDate;
    }
  });

  // Build day objects
  const days: DayObject[] = [];

  if (firstForwardDate) {
    const forwardDay = createDayObject(
      firstForwardDate,
      Array.from(forwardTimesSet),
    );
    if (forwardDay) {
      days.push(forwardDay);
    }
  }

  if (firstBackwardDate) {
    const backwardDay = createDayObject(
      firstBackwardDate,
      Array.from(backwardTimesSet),
    );
    if (backwardDay) {
      days.push(backwardDay);
    }
  }

  const backwardMapping = convertTimesMapping(
    backwardTimesByForwardTime,
    backwardTimesSet,
  ) as BackwardTimesMapping;

  return {
    days,
    backwardTimesByForwardTime: backwardMapping,
    forwardTimesByBackwardTime: invertTimesMapping(
      backwardMapping,
      forwardTimesSet,
    ),
  };
}
